import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Linkedin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, MaskLine, Overline } from "@/components/Reveal";
import { LINKEDIN_URL } from "@/components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  "Organizational consulting",
  "Leadership / team development",
  "Individual coaching",
  "Fractional / executive opportunity",
  "Recruiting / employment opportunity",
  "Speaking / facilitation",
  "Something else",
];

const MAYBES = [
  "trying to strengthen a team",
  "developing new leaders",
  "dealing with a culture problem",
  "rethinking customer experience",
  "exploring executive or fractional leadership",
  "navigating a career transition",
  "looking for individual coaching",
];

const Contact = () => {
  const [params] = useSearchParams();
  const topicParam = params.get("topic") || "";
  const [form, setForm] = useState({ name: "", email: "", company: "", topic: topicParam, message: "", website: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Message sent — it landed directly in Jocelyn's inbox.");
      setForm({ name: "", email: "", company: "", topic: "", message: "", website: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-bone border border-soot/15 px-4 py-3.5 text-sm text-ink placeholder:text-mute/50 focus:outline-none focus:border-clay focus:ring-1 focus:ring-clay transition-colors duration-300";

  return (
    <>
      <section data-testid="contact-hero" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-16 lg:grid-cols-[1fr_1fr]">
          <div>
            <Overline>Contact</Overline>
            <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.08] text-ink text-5xl sm:text-6xl lg:text-7xl">
              <MaskLine>Tell me what's</MaskLine>
              <MaskLine delay={0.15}>
                <em className="text-clay">going on.</em>
              </MaskLine>
            </h1>
            <FadeUp delay={0.3}>
              <p className="mt-8 text-base sm:text-lg leading-relaxed text-mute max-w-lg">
                You do not need to know exactly what kind of engagement you need before reaching out.
                Maybe you're:
              </p>
              <ul className="mt-6 space-y-2.5">
                {MAYBES.map((m, i) => (
                  <FadeUp key={m} delay={0.32 + 0.04 * i}>
                    <li className="flex items-start gap-3 text-base text-soot/85">
                      <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-clay" />
                      {m}
                    </li>
                  </FadeUp>
                ))}
                <FadeUp delay={0.65}>
                  <li className="flex items-start gap-3 font-serif italic text-lg text-ink">
                    <span className="mt-[11px] h-1 w-1 shrink-0 rounded-full bg-clay" />
                    or simply staring at something thinking, "We need help."
                  </li>
                </FadeUp>
              </ul>
              <p className="mt-8 font-hand text-3xl text-clay -rotate-1">Start there.</p>

              <div className="mt-12 border-t border-soot/10 pt-8">
                <p className="text-[11px] font-sans font-bold uppercase tracking-[0.25em] text-clay mb-4">
                  Prefer to lurk first?
                </p>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin-link"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-clay transition-colors link-sweep"
                >
                  <Linkedin className="h-4 w-4" /> Find Jocelyn on LinkedIn
                </a>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.2}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-cream border border-soot/10 p-7 sm:p-10"
            >
              <div className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-ink mb-2">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-ink mb-2">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-input-email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-ink mb-2">
                    Company / Organization
                  </label>
                  <input
                    id="contact-company"
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Optional"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-topic" className="block text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-ink mb-2">
                    What brings you here?
                  </label>
                  <select
                    id="contact-topic"
                    data-testid="contact-select-topic"
                    required
                    value={form.topic}
                    onChange={set("topic")}
                    className={`${inputCls} ${form.topic ? "text-ink" : "text-mute/60"}`}
                  >
                    <option value="" disabled>
                      Choose the closest fit
                    </option>
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-[11px] font-sans font-bold uppercase tracking-[0.2em] text-ink mb-2">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-input-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What's going on? The messy version is fine."
                    className={`${inputCls} resize-y`}
                  />
                </div>
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={set("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-clay px-8 py-4 text-xs font-sans font-bold uppercase tracking-[0.22em] text-bone transition-all duration-300 hover:bg-claydark disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Let's Talk"} <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-mute/70 text-center">
                  Your message goes straight to Jocelyn's inbox. No newsletters, no funnels, no nonsense.
                </p>
              </div>
            </form>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Contact;
